import axios from 'axios';
import { isSvgLogoFile } from '../utils/logoUpload';

const encodeKeyForPath = (key) => encodeURIComponent(key).replace(/%2F/g, '/');

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

/**
 * Upload an SVG logo through the server-side sanitizer.
 * Raster logos continue to use the presigned S3 flow.
 */
async function uploadSanitizedSvgLogo(file, fileType) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileType', fileType);

  const res = await axios.post('/api/uploads/svg-logo', formData, {
    headers: getAuthHeaders(),
  });

  const publicUrl =
    res?.data?.publicUrl ||
    res?.data?.file?.publicUrl ||
    (res?.data?.key ? `/api/uploads/public/${encodeKeyForPath(res.data.key)}` : null);

  if (!publicUrl) {
    throw new Error('SVG logo upload did not return a public URL');
  }

  return {
    key: res?.data?.key,
    downloadUrl: publicUrl,
  };
}

// Helper to request a presigned URL and upload a file to S3
// Returns: { key, downloadUrl }

// Upload booth logo to S3 under 'booth-logo/<userId>/<filename>'
// Returns: { key, downloadUrl }
export async function uploadBoothLogoToS3(file) {
  if (!file) throw new Error('No file provided');

  if (isSvgLogoFile(file)) {
    return uploadSanitizedSvgLogo(file, 'booth-logo');
  }

  const headers = getAuthHeaders();

  // Request presigned URL for booth-logo
  const presignRes = await axios.post(
    '/api/uploads/presign',
    {
      fileName: file.name,
      fileType: 'booth-logo',
      mimeType: file.type || 'image/jpeg',
    },
    { headers }
  );

  const { upload } = presignRes.data;
  const { url, key } = upload;

  // Upload to S3 directly using fetch.
  // Axios can add default headers/interceptors that may invalidate presigned requests.
  const putRes = await fetch(url, {
    method: 'PUT',
    mode: 'cors',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file
  });
  if (!putRes.ok) {
    throw new Error(`S3 upload failed: ${putRes.status}`);
  }

  // Confirm upload
  const completeRes = await axios.post(
    '/api/uploads/complete',
    {
      fileKey: key,
      fileType: 'booth-logo',
      fileName: file.name,
      mimeType: file.type || 'image/jpeg',
      size: file.size,
    },
    { headers }
  );

  return {
    key,
    downloadUrl: completeRes?.data?.file?.publicUrl || `/api/uploads/public/${encodeKeyForPath(key)}`,
  };
}
export async function uploadImageToS3(file, options = {}) {
  if (!file) throw new Error('No file provided');

  if (isSvgLogoFile(file)) {
    // SVG logos always go through the sanitizer and stable public proxy.
    return uploadSanitizedSvgLogo(file, 'image');
  }

  const headers = getAuthHeaders();

  // 1) Ask backend for a presigned URL
  const presignRes = await axios.post(
    '/api/uploads/presign',
    {
      fileName: file.name,
      fileType: 'image',
      mimeType: file.type || 'image/jpeg',
    },
    { headers }
  );

  const { upload } = presignRes.data;
  const { url, key } = upload;

  // 2) Upload the file to S3 directly using fetch (axios adds extra headers that break presigned URLs)
  const putRes = await fetch(url, {
    method: 'PUT',
    mode: 'cors',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file
  });
  if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status}`);

  // 3) Confirm upload so server can return a long-lived download URL
  await axios.post(
    '/api/uploads/complete',
    {
      fileKey: key,
      fileType: 'image',
      fileName: file.name,
      mimeType: file.type || 'image/jpeg',
      size: file.size,
    },
    { headers }
  );

  // Use stable proxy URL so images never expire in RTE HTML content
  const variant = options?.variant || 'rte';
  const stableUrl = variant === 'public'
    ? `/api/uploads/public/${encodeKeyForPath(key)}`
    : `/api/uploads/rte-content/${key}`;

  return {
    key,
    downloadUrl: stableUrl,
  };
}

// options.purpose: what the upload is for, shown in the SuperAdmin Activity Logs (e.g. 'queue-message')
export async function uploadAudioToS3(file, onProgress, options = {}) {
  if (!file) throw new Error('No file provided');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // 1) Ask backend for presigned URL
  const presignRes = await axios.post(
    '/api/uploads/presign',
    {
      fileName: file.name,
      fileType: 'audio',
      purpose: options.purpose,
      mimeType: file.type || 'audio/webm',
    },
    { headers }
  );

  const { upload, download } = presignRes.data;
  const { url, key } = upload;

  // 2) Upload the file to S3 directly using XMLHttpRequest for progress tracking
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percentComplete = (e.loaded / e.total) * 100;
        onProgress(percentComplete);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('S3 upload failed'));
    });
    
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });

  // 3) Confirm upload so server can return a long-lived download URL
  const completeRes = await axios.post(
    '/api/uploads/complete',
    {
      fileKey: key,
      fileType: 'audio',
      purpose: options.purpose,
      fileName: file.name,
      mimeType: file.type || 'audio/webm',
      size: file.size,
    },
    { headers }
  );

  // Use stable public proxy URL for <audio> playback (no auth required in HTML attributes)
  const publicUrl = `/api/uploads/public/${encodeKeyForPath(key)}`;

  return {
    key,
    downloadUrl: publicUrl || completeRes?.data?.file?.publicUrl || completeRes?.data?.file?.streamUrl || completeRes?.data?.file?.downloadUrl || download?.url,
  };
}

const VIDEO_MIME_BY_EXT = {
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.webm': 'video/webm',
  '.ogg': 'video/ogg',
  '.avi': 'video/x-msvideo',
  '.wmv': 'video/x-ms-wmv',
};

function getVideoMimeType(file) {
  if (file.type && /^video\//.test(file.type)) return file.type;
  const ext = (file.name || '').toLowerCase().slice(file.name.lastIndexOf('.'));
  return VIDEO_MIME_BY_EXT[ext] || 'video/mp4';
}

// options.purpose: what the upload is for, shown in the SuperAdmin Activity Logs (e.g. 'queue-message')
export async function uploadVideoToS3(file, onProgress, options = {}) {
  if (!file) throw new Error('No file provided');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const mimeType = getVideoMimeType(file);

  // 1) Ask backend for presigned URL
  const presignRes = await axios.post(
    '/api/uploads/presign',
    {
      fileName: file.name,
      fileType: 'video',
      purpose: options.purpose,
      mimeType,
    },
    { headers }
  );

  const { upload, download } = presignRes.data;
  const { url, key } = upload;

  // 2) Upload the file to S3 directly using XMLHttpRequest for progress tracking
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percentComplete = (e.loaded / e.total) * 100;
        onProgress(percentComplete);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('S3 upload failed'));
    });
    
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mimeType || 'application/octet-stream');
    xhr.send(file);
  });

  // 3) Confirm upload so server can return a long-lived download URL
  const completeRes = await axios.post(
    '/api/uploads/complete',
    {
      fileKey: key,
      fileType: 'video',
      purpose: options.purpose,
      fileName: file.name,
      mimeType,
      size: file.size,
    },
    { headers }
  );

  // Use stable public proxy URL for <video> playback (no auth required in HTML attributes)
  const publicUrl = `/api/uploads/public/${encodeKeyForPath(key)}`;

  return {
    key,
    downloadUrl: publicUrl || completeRes?.data?.file?.publicUrl || completeRes?.data?.file?.streamUrl || completeRes?.data?.file?.downloadUrl || download?.url,
  };
}
